import { useEffect, useState } from "react";
import { FiUserPlus, FiTrash2, FiMail, FiLoader } from "react-icons/fi";
import toast from "react-hot-toast";
import { api } from "../../services/api";
import { getUser } from "../../utils/auth";

const ROLE_COLORS = {
  OWNER: "bg-violet-100 text-violet-700",
  ADMIN: "bg-blue-100 text-blue-700",
  MEMBER: "bg-gray-100 text-gray-600",
};

const ALL_PAGES = [
  "dashboard", "monitoring", "inbox", "analytics", "leads",
  "sources", "appearance", "integrations", "settings", "billing", "team",
];

export default function Team() {
  const currentUser = getUser();
  const isOwner = currentUser?.role === "OWNER";

  const [members, setMembers]       = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm]             = useState({ email: "", role: "MEMBER", permissions: [] });
  const [sending, setSending]       = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        api.get("/org/members"),
        api.get("/org/invitations"),
      ]);
      setMembers(membersRes || []);
      setInvitations(invitesRes || []);
    } catch {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error("Email is required");
    setSending(true);
    try {
      await api.post("/org/invitations", form);
      toast.success(`Invitation sent to ${form.email}`);
      setForm({ email: "", role: "MEMBER", permissions: [] });
      setInviteOpen(false);
      load();
    } catch (err) {
      toast.error(err.message || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  const revokeInvite = async (id) => {
    try {
      await api.delete(`/org/invitations/${id}`);
      toast.success("Invitation revoked");
      load();
    } catch {
      toast.error("Failed to revoke invitation");
    }
  };

  const removeMember = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await api.delete(`/org/invitations/members/${id}`);
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(err.message || "Failed to remove member");
    }
  };

  const togglePermission = (page) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(page)
        ? f.permissions.filter((p) => p !== page)
        : [...f.permissions, page],
    }));
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="text-sm text-gray-500 mt-1">Manage who has access and what they can see.</p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition"
        >
          <FiUserPlus size={15} /> Invite Member
        </button>
      </div>

      {/* Invite modal */}
      {inviteOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={sendInvite} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Invite Team Member</h2>

            <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="teammate@company.com"
              className="w-full mb-4 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />

            <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              className="w-full mb-4 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {isOwner && <option value="ADMIN">Admin (full access)</option>}
              <option value="MEMBER">Member (restricted)</option>
            </select>

            {form.role === "MEMBER" && (
              <>
                <label className="block text-xs font-medium text-gray-600 mb-2">Page Access</label>
                <div className="grid grid-cols-2 gap-1.5 mb-4">
                  {ALL_PAGES.map((page) => (
                    <label key={page} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer capitalize">
                      <input
                        type="checkbox"
                        checked={form.permissions.includes(page)}
                        onChange={() => togglePermission(page)}
                        className="accent-primary"
                      />
                      {page}
                    </label>
                  ))}
                </div>
              </>
            )}

            <div className="flex gap-3 mt-2">
              <button type="button" onClick={() => setInviteOpen(false)} className="flex-1 px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="submit"
                disabled={sending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-60"
              >
                {sending ? <FiLoader className="animate-spin" size={14} /> : <FiMail size={14} />}
                {sending ? "Sending…" : "Send Invite"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Members table */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-6">
        <div className="px-5 py-3 border-b bg-gray-50">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Active Members</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400 text-sm">Loading…</div>
        ) : members.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">No members yet.</div>
        ) : (
          <div className="divide-y">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-4 px-5 py-3.5">
                <div className="w-9 h-9 rounded-full bg-primary-light flex items-center justify-center text-primary font-bold text-sm shrink-0">
                  {m.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {m.name} {m.id === currentUser?.id && <span className="text-xs text-gray-400">(you)</span>}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{m.email}</p>
                </div>
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${ROLE_COLORS[m.role] ?? ROLE_COLORS.MEMBER}`}>
                  {m.role}
                </span>
                {m.role !== "OWNER" && m.id !== currentUser?.id && (
                  <button onClick={() => removeMember(m.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition" title="Remove">
                    <FiTrash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b bg-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Pending Invitations</p>
          </div>
          <div className="divide-y">
            {invitations.map((inv) => (
              <div key={inv.id} className="flex items-center gap-4 px-5 py-3.5">
                <div className="w-9 h-9 rounded-full bg-yellow-50 border border-yellow-200 flex items-center justify-center shrink-0">
                  <FiMail size={14} className="text-yellow-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{inv.email}</p>
                  <p className="text-xs text-gray-400">
                    Invited as {inv.role} · Expires {new Date(inv.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700">Pending</span>
                <button onClick={() => revokeInvite(inv.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition" title="Revoke">
                  <FiTrash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
